// Login/Logout für den Admin-Editor über Firebase Auth (E-Mail + Passwort).
// Wird nur benutzt, wenn Firebase konfiguriert ist (siehe isFirebaseConfigured in
// firebase.js) - ohne Konfiguration greift AdminSpeiseplan.jsx auf einen lokalen
// Test-Login zurück und ruft diese Funktionen gar nicht erst auf.
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { firebaseAuth, requireFirebaseAuth } from "./firebase";

// Firebase liefert nur technische Fehlercodes zurück (z.B. "auth/wrong-password").
// Hier übersetzen wir die häufigsten davon in verständliche deutsche Meldungen
// fürs Login-Formular. Unbekannte Codes fallen auf error.message zurück.
const AUTH_ERROR_MESSAGES = {
  "auth/invalid-credential": "E-Mail-Adresse oder Passwort ist nicht korrekt.",
  "auth/invalid-email": "Bitte eine gültige E-Mail-Adresse eingeben.",
  "auth/missing-password": "Bitte ein Passwort eingeben.",
  "auth/too-many-requests": "Zu viele Versuche. Bitte versuche es später erneut.",
  "auth/user-disabled": "Dieser Account wurde deaktiviert.",
  "auth/user-not-found": "Für diese E-Mail-Adresse wurde kein Account gefunden.",
  "auth/wrong-password": "E-Mail-Adresse oder Passwort ist nicht korrekt.",
};

function createAuthError(error) {
  const message =
    AUTH_ERROR_MESSAGES[error?.code] || error?.message || "Authentifizierung fehlgeschlagen.";
  const authError = new Error(message);
  authError.code = error?.code;
  return authError;
}

function normalizeEmail(email) {
  return String(email || "").trim();
}

export async function loginWithEmail({ email, password }) {
  const auth = requireFirebaseAuth();

  try {
    const credential = await signInWithEmailAndPassword(
      auth,
      normalizeEmail(email),
      String(password || "")
    );
    // reload() holt den aktuellen emailVerified-Status vom Server, falls die
    // Verifizierung erst kurz vor dem Login passiert ist (sonst könnte hier
    // noch ein veralteter, gecachter Stand anliegen).
    await credential.user.reload();
    return credential.user;
  } catch (error) {
    throw createAuthError(error);
  }
}

export async function logout() {
  if (!firebaseAuth) {
    return;
  }

  try {
    await signOut(firebaseAuth);
  } catch (error) {
    throw createAuthError(error);
  }
}

// Admin-Requests brauchen bei jedem Aufruf einen frischen ID-Token als Bearer-Token.
// Firebase cached/erneuert den Token intern, forceRefresh ist normalerweise nicht nötig.
export async function getCurrentUserToken({ forceRefresh = false } = {}) {
  const auth = requireFirebaseAuth();
  const user = auth.currentUser;

  if (!user) {
    return null;
  }

  try {
    return await user.getIdToken(forceRefresh);
  } catch (error) {
    throw createAuthError(error);
  }
}

export function isEmailVerified() {
  return Boolean(firebaseAuth?.currentUser?.emailVerified);
}
